import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Calendar, Paperclip } from "lucide-react";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import { useStudentCourseDetail } from "./hooks/useStudentCourseDetail";
import {
  formatCourseDate,
  getCourseAttachments,
  getCourseClassLabel,
  getCourseContent,
  getCourseDate,
  getCourseSubject,
  getCourseTitle,
} from "./courseFieldUtils";
import "./StudentCourses.css";

const isUrl = (value) => /^https?:\/\//i.test(value);

// Renders only its own content — StudentPortalLayout already provides the
// sidebar/header shell around every route.
const StudentCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { course, isLoading } = useStudentCourseDetail(id);

  const backButton = (
    <Button variant="outline-secondary" icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
      Back to Courses
    </Button>
  );

  if (isLoading) {
    return (
      <div>
        <div className="sp-course-detail__backbar">{backButton}</div>
        <Loader />
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <div className="sp-course-detail__backbar">{backButton}</div>
        <div className="sp-card mt-3">
          <p className="mb-0">This course could not be found.</p>
        </div>
      </div>
    );
  }

  const subject = getCourseSubject(course);
  const classLabel = getCourseClassLabel(course);
  const date = getCourseDate(course);
  const content = getCourseContent(course);
  const attachments = getCourseAttachments(course);

  return (
    <div>
      <div className="sp-course-detail__backbar">{backButton}</div>

      <div className="sp-card sp-course-hero mt-3">
        <span className="sp-icon-tile" data-tone="primary">
          <BookOpen size={20} />
        </span>
        <div className="sp-course-hero__body">
          <h2 className="mb-1">{getCourseTitle(course)}</h2>
          {(subject || classLabel || date) && (
            <div className="sp-course-hero__facts">
              {(subject || classLabel) && <span>{[subject, classLabel].filter(Boolean).join(" · ")}</span>}
              {date && (
                <span>
                  <Calendar size={14} /> {formatCourseDate(date)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="sp-card mt-3">
        <h3 className="sp-section-title">Lesson Content</h3>
        {content ? (
          <p className="sp-course-detail__content mb-0">{content}</p>
        ) : (
          <p className="text-muted mb-0">No further details have been added for this course.</p>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="sp-card mt-3">
          <h3 className="sp-section-title">Attachments</h3>
          <ul className="sp-course-detail__attachments">
            {attachments.map((attachment) => (
              <li key={attachment}>
                <span className="sp-icon-tile sp-icon-tile--sm" data-tone="secondary">
                  <Paperclip size={14} />
                </span>
                {isUrl(attachment) ? (
                  <a href={attachment} target="_blank" rel="noopener noreferrer">
                    {attachment}
                  </a>
                ) : (
                  <span>{attachment}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StudentCourseDetail;
